import { useEffect, useState } from 'react';
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import {
  getApiKeyRoles,
  ROLES_PAGE_SIZE,
  type ApiKeyRole,
} from './api-key-roles.service';
import { getRoleRuleDefaults } from './api-key-scopes';

export function ApiKeyRoleCombobox({
  label,
  organizationId,
  value,
  onChange,
}: {
  label: string;
  organizationId: string;
  value: string;
  onChange: (value: string, role: ApiKeyRole) => void;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const trimmedSearch = search.trim();
    if (trimmedSearch === debouncedSearch) return;
    const timeout = setTimeout(() => {
      setDebouncedSearch(trimmedSearch);
      setPage(0);
    }, 350);
    return () => clearTimeout(timeout);
  }, [search, debouncedSearch]);

  const { data, isPending, isError, isFetching, refetch } = useQuery({
    queryKey: ['api-key-role-options', organizationId, page, debouncedSearch],
    queryFn: () => getApiKeyRoles(organizationId, page, debouncedSearch),
    enabled: open && !!organizationId,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / ROLES_PAGE_SIZE));
  const eligibleRoles = data?.items.filter((role) => getRoleRuleDefaults(role) !== null) ?? [];

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setSearch('');
          setDebouncedSearch('');
          setPage(0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          variant="outline"
          className="h-11 w-full justify-between px-3 font-normal"
          onClick={(event) =>
            setPortalContainer(event.currentTarget.closest<HTMLElement>('[role="dialog"]'))
          }
        >
          <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
            {value || 'Select a role'}
          </span>
          <CaretDownIcon size={16} className="shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        portalContainer={portalContainer}
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <div className="border-b p-2">
          <Input
            type="search"
            aria-label={`Search ${label}`}
            placeholder="Search roles"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="h-56 overflow-y-auto p-1" aria-busy={isFetching}>
          {!organizationId ? (
            <p className="p-3 text-sm text-muted-foreground">Select an organization first.</p>
          ) : isPending ? (
            <p role="status" className="p-3 text-sm text-muted-foreground">
              Loading roles…
            </p>
          ) : isError ? (
            <div role="alert" className="space-y-2 p-3 text-sm">
              <p>Couldn’t load roles.</p>
              <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
                Retry roles
              </Button>
            </div>
          ) : !eligibleRoles.length ? (
            <p className="p-3 text-sm text-muted-foreground">No roles found.</p>
          ) : (
            <div role="listbox" aria-label={`${label} options`}>
              {eligibleRoles.map((role) => (
                <button
                  key={role.itemId}
                  type="button"
                  role="option"
                  aria-selected={value === role.slug}
                  aria-label={`${role.name} ${role.slug}`}
                  className="flex w-full flex-col rounded px-3 py-2 text-left text-sm hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none"
                  onClick={() => {
                    onChange(role.slug, role);
                    setOpen(false);
                  }}
                >
                  <span>{role.name}</span>
                  <code className="text-xs text-muted-foreground">{role.slug}</code>
                </button>
              ))}
            </div>
          )}
        </div>
        <nav
          aria-label={`${label} pagination`}
          className="flex items-center justify-between border-t px-2 py-1.5"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Previous ${label} page`}
            disabled={page === 0 || isFetching}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            <CaretLeftIcon size={16} />
          </Button>
          <span className="text-xs text-muted-foreground">
            {page + 1} of {totalPages}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Next ${label} page`}
            disabled={isFetching || !data || (page + 1) * ROLES_PAGE_SIZE >= data.totalCount}
            onClick={() => setPage((current) => current + 1)}
          >
            <CaretRightIcon size={16} />
          </Button>
        </nav>
      </PopoverContent>
    </Popover>
  );
}
